import { cn } from "cn";
import { capitalize } from "lodash-es";
import type { PropsWithChildren, ReactNode } from "react";
import { BadgeDot } from "@/components/ui/badge/badge-dot";
import { SeparatorDot } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Typography } from "@/components/ui/typography";
import {
  DISPLAY_TIME,
  formatDate,
  formatMessageTimestamp,
} from "@/libs/date-fns";
import { isMessageEdited } from "@/libs/message-edited";
import { getPlaceRoleLabel } from "@/libs/place-role-labels";
import { MessageViaClient } from "@/types/openapi-enum";

type MessageProps = PropsWithChildren & {
  className?: string;
  avatar: ReactNode;
  userCreateName: ReactNode;
  viaClient: string;
  createdAt: string;
  updatedAt?: string;
  isNew?: boolean;
  isHighlight?: boolean;
  userPlaceRole?: string;
  renderReadReceipts?: () => ReactNode;
};

export const Message = ({
  className,
  avatar,
  userCreateName,
  viaClient,
  createdAt,
  updatedAt,
  isNew,
  isHighlight,
  userPlaceRole,
  renderReadReceipts,
  children,
  ...props
}: MessageProps) => {
  return (
    <div
      data-test="message-item"
      className={cn(
        /* sorted */
        "@container/message",
        "relative flex gap-3",
        "px-3 py-3",
        "xl:px-4",
        className,
        isHighlight && "animate-highlight duration-5000",
      )}
      {...props}
    >
      <div className="relative">
        {isNew && (
          <BadgeDot
            data-test="unread-message-icon"
            color="purple"
            className={cn(
              /* sorted */
              "absolute top-4 -left-1",
              "size-1.5",
              "-translate-x-full -translate-y-1/2",
            )}
          />
        )}
        {avatar}
      </div>
      <div className="grow">
        <div className="flex items-center gap-1">
          <span className="text-sm font-semibold text-text-dark-max">
            {userCreateName}
          </span>
          {!!userPlaceRole && (
            <Tooltip>
              <TooltipTrigger>
                <p className="text-sm text-text-dark-high">
                  ({getPlaceRoleLabel(userPlaceRole)})
                </p>
              </TooltipTrigger>
              <TooltipContent side="top">
                <p className="text-xs">Place role</p>
              </TooltipContent>
            </Tooltip>
          )}
          {renderReadReceipts?.()}
          <Tooltip>
            <TooltipTrigger>
              <Typography
                className="text-xs text-text-dark-high -mb-0.5"
                data-test="message-timestamp"
              >
                {formatDate(createdAt, DISPLAY_TIME)}
              </Typography>
            </TooltipTrigger>
            <TooltipContent>
              <Typography
                size="xs"
                className="text-text-light"
                data-test="message-timestamp-tooltip"
              >
                {formatMessageTimestamp(createdAt)}
              </Typography>
            </TooltipContent>
          </Tooltip>
          {!!updatedAt && isMessageEdited({ createdAt, updatedAt }) && (
            <Tooltip>
              <TooltipTrigger className="hidden md:flex">
                <Typography
                  size="xs"
                  className="text-text-dark-high"
                  data-test="message-edited"
                >
                  (Edited)
                </Typography>
              </TooltipTrigger>
              <TooltipContent>
                <Typography
                  size="xs"
                  className="text-text-light"
                  data-test="message-edited-tooltip"
                >
                  {formatMessageTimestamp(updatedAt)}
                </Typography>
              </TooltipContent>
            </Tooltip>
          )}
          {viaClient && viaClient.toUpperCase() !== MessageViaClient.Webapp && (
            <>
              <SeparatorDot className="hidden md:block -mb-0.5" />
              <Typography
                size="xs"
                className={cn(
                  /* sorted */
                  "hidden",
                  "-mb-0.5",
                  "text-text-dark-high",
                  "md:block",
                )}
              >
                via {capitalize(viaClient)}
              </Typography>
            </>
          )}
        </div>
        {children}
      </div>
    </div>
  );
};
